import { Attachment } from "@/api/attachment";
import { SupportedLocale } from "@/i18n";
import {
  computeFieldMultipleprintDimensionImage,
  computeFieldMultipleprintDimensions,
  computeFieldMultipleprintSummaryDimensions,
  computeFieldMultipleprintInscriptionDos,
  computeFieldMultipleprintRelatedAuthors,
  computeFieldMultipleprintSignature,
  computeFieldMultipleprintSummary,
  computeFieldMultipleprintSupportTechnique,
  computeFieldRelatedAuctions,
  computeFieldRelatedBibliographiesMultipleprints,
  computeFieldRelatedExhibitionsMultipleprint,
} from "@/lib/computeFields";
import { useAuth } from "@/context/auth/AuthContext";
import { buildMultipleprintProvenance } from "@/lib/multipleprint-provenance";
import { filterPublishedMultipleprintInAuction } from "@/lib/multipleprint-auctions";
import { getDefaultAttachment } from "@/lib/image";
import { Multipleprint } from "@/lib/types";
import {
  ComputedFieldsMultipleprint,
  MultipleprintDisplayData,
} from "@/pages/dashboard/multipleprint-overview/types";
import { useMemo } from "react";

export interface UseMultipleprintPageDataProps {
  data: Multipleprint | null | undefined;
  attachments: Attachment[];
  locale: SupportedLocale;
  t: (key: string, options?: Record<string, unknown>) => string;
  itemType?: "MULTIPLE" | "EXEMPLAIRE";
}

export interface UseMultipleprintPageDataResult {
  displayData: MultipleprintDisplayData;
  mainImage: Attachment | null;
  otherImages: Attachment[];
  computedFields: ComputedFieldsMultipleprint;
}

export function useMultipleprintPageData({
  data,
  attachments,
  locale,
  t,
  itemType = "MULTIPLE",
}: UseMultipleprintPageDataProps): UseMultipleprintPageDataResult {
  const { instanceData } = useAuth();
  const provenanceMode = instanceData?.provenanceMode ?? false;
  const isExemplaire = itemType === "EXEMPLAIRE";
  const mainImage = useMemo(
    () =>
      attachments?.length
        ? getDefaultAttachment(
            attachments,
            isExemplaire ? "multiplesample" : "multipleprint",
          )
        : null,
    [attachments, isExemplaire]
  );

  const otherImages = useMemo(() => {
    if (!attachments?.length || !mainImage) return [];
    return attachments
      .filter((a) => a.id !== mainImage.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [attachments, mainImage]);

  const overviewSummaryCalculated = useMemo(
    () =>
      data
        ? computeFieldMultipleprintSummary(data, {
            language: locale,
            translation: t,
            lineBreak: true,
          })
        : "",
    [data, locale, t]
  );

  const artistsCalculated = useMemo(
    () =>
      data
        ? computeFieldMultipleprintRelatedAuthors(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, locale, t]
  );

  const dimensionsSummaryCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintSummaryDimensions(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t],
  );

  const dimensionsCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintDimensions(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t]
  );

  const dimensionsImageCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintDimensionImage(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t]
  );

  const supportTechniqueCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintSupportTechnique(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t]
  );

  const signatureCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintSignature(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t]
  );

  const inscriptionDosCalculated = useMemo(
    () =>
      data && !isExemplaire
        ? computeFieldMultipleprintInscriptionDos(data, {
            language: locale,
            translation: t,
          })
        : "",
    [data, isExemplaire, locale, t]
  );

  const exhibitionsCalculated = useMemo(
    () =>
      !isExemplaire &&
      data?.multipleprintInExhibition?.filter(
        (item) => item.exhibition?.expositionsPublicationStatus === "published"
      ).length
        ? computeFieldRelatedExhibitionsMultipleprint(
            {
              relatedExhibitions: data.multipleprintInExhibition.filter(
                (item) => item.exhibition?.expositionsPublicationStatus === "published"
              ),
            },
            { language: locale, translation: t }
          )
        : "",
    [data, isExemplaire, locale, t]
  );

  const bibliographyCalculated = useMemo(() => {
    const publishedBibliographies =
      data?.multipleprintInBibliography?.filter(
        (m) => m.bibliography?.bibliographiePublicationStatus === "published"
      ) ?? [];
    return !isExemplaire && publishedBibliographies.length
      ? computeFieldRelatedBibliographiesMultipleprints(
          { relatedBibliographies: publishedBibliographies },
          { language: locale, translation: t }
        )
      : "";
  }, [data, isExemplaire, locale, t]);

  const publishedParentAuctions = useMemo(
    () =>
      !isExemplaire
        ? filterPublishedMultipleprintInAuction(data?.multipleprintInAuction)
        : [],
    [data?.multipleprintInAuction, isExemplaire],
  );

  const historyCalculated = useMemo(() => {
    if (isExemplaire || !data) return null;
    return buildMultipleprintProvenance({
      history: data.history ?? [],
      auctions: provenanceMode ? publishedParentAuctions : [],
      t,
    });
  }, [data, isExemplaire, provenanceMode, publishedParentAuctions, t]);

  const auctionCalculated = useMemo(
    () =>
      !isExemplaire &&
      !provenanceMode &&
      publishedParentAuctions.length
        ? computeFieldRelatedAuctions(
            {
              relatedAuctions: publishedParentAuctions.map((item) => ({
                auction: item.auction,
                oeuvresDansVentesDescriptionNumeroLot:
                  item.ventesDansEstampesMultiplesDescriptionNumeroLot,
                oeuvresDansVentesDescriptionReproduitPage:
                  item.ventesDansEstampesMultiplesDescriptionReproduitPage,
                oeuvresDansVentesDescriptionNonReproduit: false,
              })),
            },
            { language: locale, translation: t },
          )
        : "",
    [isExemplaire, locale, provenanceMode, publishedParentAuctions, t],
  );

  const computedFields: ComputedFieldsMultipleprint = useMemo(
    () => ({
      overviewSummaryCalculated,
      artistsCalculated,
      dimensionsSummaryCalculated,
      dimensionsCalculated,
      dimensionsImageCalculated,
      supportTechniqueCalculated,
      signatureCalculated,
      inscriptionDosCalculated,
      exhibitionsCalculated,
      bibliographyCalculated,
      auctionCalculated,
      historyCalculated,
    }),
    [
      overviewSummaryCalculated,
      artistsCalculated,
      dimensionsSummaryCalculated,
      dimensionsCalculated,
      dimensionsImageCalculated,
      supportTechniqueCalculated,
      signatureCalculated,
      inscriptionDosCalculated,
      exhibitionsCalculated,
      bibliographyCalculated,
      auctionCalculated,
      historyCalculated,
    ],
  );

  if (!data) {
    return {
      displayData: {} as MultipleprintDisplayData,
      mainImage: null,
      otherImages: [],
      computedFields: {
        overviewSummaryCalculated: "",
        artistsCalculated: "",
        dimensionsSummaryCalculated: "",
        dimensionsCalculated: "",
        dimensionsImageCalculated: "",
        supportTechniqueCalculated: "",
        signatureCalculated: "",
        inscriptionDosCalculated: "",
        exhibitionsCalculated: "",
        bibliographyCalculated: "",
        auctionCalculated: "",
        historyCalculated: null,
      },
    };
  }

  return {
    displayData: data,
    mainImage: mainImage ?? null,
    otherImages,
    computedFields,
  } as UseMultipleprintPageDataResult;
}
